import { gsap } from "gsap";
import { CustomEase } from "gsap/CustomEase";
import { Draggable } from "gsap/Draggable";
import { EaselPlugin } from "gsap/EaselPlugin";
import { ExpoScaleEase } from "gsap/EasePack";
import { Flip } from "gsap/Flip";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import { PixiPlugin } from "gsap/PixiPlugin";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { TextPlugin } from "gsap/TextPlugin";
import * as PIXI from "pixi.js";
import { Api } from "../api/Api";
import { ResultCode } from "../api/common/controller/net/constant/Constants";
import { SessionTimeOutEvent } from "../api/common/controller/net/response/event/SessionTimeOutEvent";
import { AppCore } from "../api/core/AppCore";
import { AppLoader } from "../loader/AppLoader";
import { GameConfigHandler } from "../loader/config/GameConfigHandler";
import { CurrencyLoader } from "../loader/currency/CurrencyLoader";
import { ErrorCodesHandler } from "../loader/errorCodes/ErrorCodesHandler";
import { LoaderEvent } from "../loader/event/LoaderEvent";
import { LangLoader } from "../loader/lang/LangLoader";
import { WrapperLoader } from "../loader/wrapper/WrapperLoader";
import { Dictionary } from "../util/collection/Dictionary";
import { CurrencyUtil } from "../util/currency/CurrencyUtil";
import { DeviceUtil } from "../util/device/DeviceUtil";
import { Logger, LogLevel } from "../util/log/Logger";
import { WebUtil } from "../util/web/WebUtil";
import { AppData } from "./AppData";
import { AppEvent, DeployType, GameErrorEvent, Modules } from "./Constants";
import { TimeData } from "./control/data/TimeData";
import { WrapperController } from "./controller/WrapperController";
import { FactoryManager } from "./factory/FactoryManager";
import { FullScreenManager } from "./manager/FullScreenManager";
import { LanguageManager } from "./manager/LanguageManager";
import { RendererProperties } from "./renderer/properties/RendererProperties";
import { Renderer } from "./renderer/Renderer";
import { AbstractLoader } from "./view/display/AbstractLoader";
import { FullScreen } from "./view/display/FullScreen";
/**
 * App.ts
 */
export class App {
    constructor(version, releaseVersion, CLS, ...args) {
        this.CLS = CLS;
        AppData.VERSION = version;
        AppData.RELEASE_VERSION = releaseVersion;
        AppData.DEPLOY_TYPE = DeployType.WEB;
        Logger.forceLog("Release Version: " + AppData.RELEASE_VERSION);
        PIXI.TextureGCSystem.defaultCheckCountMax = 12000;
        this.initialise();
    }
    initialise() {
        this.views = new Dictionary();
        this.controllers = [];
        this.registerGsapPlugins();
        this.core = AppCore.getInstance();
        this.core.setTime(new TimeData());
        this.languageManager = LanguageManager.getInstance();
        this.factoryManager = FactoryManager.getInstance();
        this.loadWrapper();
        Logger.addObjectRef(this, "App");
        window.PIXI = PIXI;
        window.gsap = gsap;
    }
    loadWrapper() {
        this.wrapperLoader = new WrapperLoader();
        this.wrapperLoader.onSuccess(this.wrapperScriptLoaded, this);
        this.wrapperLoader.onFailed(this.wrapperScriptFailed, this);
        this.wrapperLoader.load();
    }
    wrapperScriptLoaded() {
        console.log("App: Wrapper Script Load Succeeded");
        this.createWrapperController();
    }
    wrapperScriptFailed() {
        console.log("App: Wrapper Script Load Failed");
    }
    handleWrapperReady() {
        ErrorCodesHandler.getInstance().load(null);
        let currencyLoader = new CurrencyLoader();
        currencyLoader.load(() => {
            let wrapperConfig = this.wrapperController.getWrapperConfig();
            let operatorParameters = wrapperConfig.getOperatorParameters();
            this.languageManager.setLocale(operatorParameters.getLocale());
            new this.CLS(AppData.VERSION, () => {
                this.factory = this.factoryManager.getFactory();
                this.factory.setWrapperConfig(wrapperConfig);
                let operatorConfig = this.factory.getOperatorConfig();
                AppData.LITE_SERVER = operatorConfig.getLiteAssetServer?.();
                console.log(wrapperConfig);
                this.wrapperController.setFactory(this.factory);
                this.model = this.factory.getModel();
                this.model.setRoomName(wrapperConfig.getLogin().getRoomName());
                this.model.setWrapperConfig(wrapperConfig);
                this.setCurrency();
                this.setDemoPlay();
                this.createFullScreenManager();
                this.createRendererProperties();
                this.createRenderer();
                this.setVerticalOffset(wrapperConfig.getOperatorConfig());
                this.loadJSONLang();
                this.addEventListeners();
                this.dispatchCore(AppEvent.WRAPPER_READY);
            });
        });
    }
    setVerticalOffset(operatorConfig) {
        if (operatorConfig.hasVerticalOffset()) {
            let verticalOffset = operatorConfig.getVerticalOffset();
            this.renderer.setVerticalOffSet(verticalOffset);
        }
    }
    handleWrapperFailed() {
        console.log("Wrapper Failed in App");
        let wrapperConfig = this.wrapperController.getWrapperConfig();
        let operatorParameters = wrapperConfig.getOperatorParameters();
        AppData.LITE_SERVER = wrapperConfig.getOperatorConfig().getLiteAssetServer?.();
        this.factory.setWrapperConfig(wrapperConfig);
        this.model.setWrapperConfig(wrapperConfig);
        this.languageManager.setLocale(operatorParameters.getLocale());
        this.createRendererProperties();
        this.createRenderer();
        this.loadJSONLang();
    }
    dispatchWrapperFailed() {
        let resultCode = this.model.getErrorData().getCode();
        let event = GameErrorEvent.SESSION_TIME_OUT;
        if (resultCode == ResultCode.USER_NOT_AUTHENTICATED) {
            event = GameErrorEvent.USER_NOT_AUTHENTICATED;
        }
        else if (resultCode == ResultCode.REGULATORY_MESSAGE) {
            event = GameErrorEvent.REGULATORY_MESSAGE;
        }
        else if (resultCode == ResultCode.GAME_ACCESS_DENIED) {
            event = GameErrorEvent.GAME_ACCESS_DENIED;
        }
        else if (resultCode == ResultCode.UNKNOWN_PROMOTION_CODE) {
            event = GameErrorEvent.UNKNOWN_PROMOTION_CODE;
        }
        if (resultCode == ResultCode.CURRENCY_NOT_SUPPORTED) {
            event = GameErrorEvent.CURRENCY_NOT_SUPPORTED;
        }
        this.dispatchCore(event);
    }
    addEventListeners() {
        this.registerCore(LoaderEvent.LOADER_REMOVE, this.handleRemoveLoader, this);
        this.registerCore(SessionTimeOutEvent.TIME_OUT, this.handleSessionTimeout, this);
        this.registerCore(LoaderEvent.COMPLETE, this.handleLoaderFinishedLoading, this);
    }
    createFullScreenManager() {
        let operatorConfig = this.factory.getOperatorConfig();
        console.log(this.factory.getOperatorConfig());
        if (operatorConfig.isFullscreenEnabled()) {
            this.fullScreenManager = FullScreenManager.getInstance().getManager();
        }
    }
    createRendererProperties() {
        let device = DeviceUtil.getRendererDevice();
        let viewport = this.getViewport();
        this.rendererProperties = new RendererProperties(device, viewport);
        if (this.fullScreenManager && this.fullScreenManager.isBrim()) {
            this.rendererProperties.preventTouchMove = false;
        }
    }
    getViewport() {
        if (window.innerWidth > window.innerHeight) {
            return this.factory.getLandscapeViewport();
        }
        else {
            return this.factory.getPortraitViewport();
        }
    }
    createRenderer() {
        this.renderer = Renderer.getInstance().initialise(this.rendererProperties);
        this.renderer.createBackground(this.factory.getLandscapeTileLocation(), this.factory.getPortraitTileLocation());
    }
    getRenderer() {
        return this.renderer;
    }
    async createAppLoader() {
        await PIXI.Assets.init({ preferences: { preferCreateImageBitmap: false } }); //see https://github.com/pixijs/pixijs/issues/9978
        this.appLoader = new AppLoader(this.factory.getConfigPath(), AppData.VERSION);
        this.appLoader.onPreloaderReady(this.handlePreloaderReady, this);
        this.appLoader.onPreloaderComplete(this.handlePreloaderComplete, this);
        this.appLoader.onLoaderReady(this.handleLoaderReady, this);
        this.appLoader.onLoaderProgress(this.handleLoadProgress, this);
        this.appLoader.onLoaderComplete(this.handleLoaderComplete, this);
        this.appLoader.load();
    }
    loadJSONLang() {
        const loader = new LangLoader(this.wrapperController.getWrapperConfig());
        loader.load();
        loader.onComplete = this.handleLangComplete.bind(this);
    }
    handleLangComplete() {
        this.createAppLoader();
    }
    createGameConfigLoader() {
        this.gameConfigLoader = new GameConfigHandler(this.factory);
        this.gameConfigLoader.onComplete(this.handleGameConfigLoaded, this);
        this.gameConfigLoader.load();
    }
    createWrapperController() {
        this.wrapperController = new WrapperController(this.factory);
        this.wrapperController.onWrapperReady(this.handleWrapperReady, this);
        this.wrapperController.onWrapperFailed(this.handleWrapperFailed, this);
    }
    handlePreloaderReady() {
        this.createPreloader();
    }
    handlePreloaderComplete() {
        this.createFullScreen();
        this.createOrientationController();
    }
    //Loader at 100%
    handleLoaderFinishedLoading() {
        this.reparentView(Modules.LOADER);
        this.reparentView(Modules.FULL_SCREEN);
    }
    handleLoaderReady() {
        this.createLoader();
        this.createDialogManager();
        this.createOrientation();
        this.removeView(Modules.PRELOADER, true);
        this.reparentView(Modules.FULL_SCREEN);
    }
    handleLoadProgress() {
        let loader = this.getView(Modules.LOADER);
        let progress = this.appLoader.getProgress();
        if (loader instanceof AbstractLoader) {
            loader.updateProgress(progress);
        }
        this.model.setLoaderProgress(progress);
        this.dispatchCore(LoaderEvent.PROGRESS);
    }
    createPreloader() {
        let preloader = this.factory.createPreloader();
        if (preloader) {
            this.views.setValue(Modules.PRELOADER, preloader);
            this.addView(Modules.PRELOADER);
        }
    }
    createOrientationController() {
        let orientationController = this.factory.createOrientationController();
        if (orientationController) {
            this.controllers.push(orientationController);
        }
    }
    createFullScreen() {
        if (this.fullScreenManager) {
            if (!this.fullScreenManager.isBrim()) {
                this.views.setValue(Modules.FULL_SCREEN, new FullScreen(this.fullScreenManager));
                this.addView(Modules.FULL_SCREEN);
            }
            this.fullScreenManager.start();
        }
    }
    createLoader() {
        let loader = this.factory.createLoader();
        this.views.setValue(Modules.LOADER, loader);
        this.addView(Modules.LOADER);
    }
    createDialogManager() {
        this.controllers.push(this.factory.createDialogController());
        let dialogManager = this.factory.getDialogManager();
        this.views.setValue(Modules.DIALOG_MANAGER, dialogManager);
        this.addView(Modules.DIALOG_MANAGER);
    }
    createOrientation() {
        let operatorConfig = this.factory.getOperatorConfig();
        if (operatorConfig.isOrientationEnabled()) {
            this.views.setValue(Modules.ORIENTATION, this.factory.createOrientation());
            this.addView(Modules.ORIENTATION);
        }
    }
    //Loader has completed creation (i.e. preloader at 100%, loader at 0%)
    handleLoaderComplete() {
        if (this.model.getErrorData().hasError()) {
            this.dispatchWrapperFailed();
        }
        else {
            this.create();
        }
    }
    handleGameConfigLoaded() {
        if (this.model.getErrorData().hasError()) {
            this.dispatchWrapperFailed();
        }
        else {
            this.factory.setGameConfig(this.gameConfigLoader.getActiveConfig());
        }
    }
    setCurrency() {
        let wrapperConfig = this.wrapperController.getWrapperConfig();
        let currency = wrapperConfig.getLogin().getCurrency();
        if (!currency) {
            currency = wrapperConfig.getOperatorParameters().getCurrency();
        }
        if (currency) {
            CurrencyUtil.setFormatByCode(currency);
        }
    }
    setDemoPlay() {
        let wrapperConfig = this.wrapperController.getWrapperConfig();
        this.model.setDemoPlay(wrapperConfig.getOperatorParameters().isDemoPlay());
    }
    register() {
        let operatorConfig = this.factory.getOperatorConfig();
        if (operatorConfig.isRealityCheckEnabled()) {
            this.registerRealityCheck(operatorConfig.isWinLossEnabled());
        }
        Logger.setLogLevel(LogLevel.SILENT);
    }
    registerRealityCheck(winLossEnabled) {
        Api.onRealityCheckRefresh(this.handleRealityCheckRefresh, this);
        if (winLossEnabled) {
            Api.onRealityCheckTimeout(true, this.handleRealityCheckWinLoss, this);
        }
        else {
            Api.onRealityCheckTimeout(false, this.handleRealityCheck, this);
        }
    }
    create() {
        this.createControllers();
        this.createViews();
    }
    createControllers() {
        this.controllers = this.controllers.concat(this.factory.createControllers());
    }
    createViews() {
        let game = this.factory.createGame();
        this.views.setValue(Modules.GAME, game);
    }
    handleSessionTimeout() {
        this.dispatchCore(GameErrorEvent.SESSION_TIME_OUT);
    }
    handleBalanceUpdated() {
        this.dispatchCore(AppEvent.BALANCE);
    }
    handleRealityCheckWinLoss(result) {
        this.model.setWinLoss(result);
        this.dispatchCore(GameErrorEvent.REALITY_CHECK);
    }
    handleRealityCheck() {
        this.dispatchCore(GameErrorEvent.REALITY_CHECK);
    }
    handleRealityCheckRefresh(result) {
        this.model.setRealityCheckDetails(result);
    }
    handleRemoveLoader() {
        this.removeView(Modules.LOADER, true);
        this.addView(Modules.GAME);
        this.reparentView(Modules.DIALOG_MANAGER);
        this.reparentView(Modules.ORIENTATION);
        this.reparentView(Modules.FULL_SCREEN);
        const replayId = WebUtil.getUrlParameter("replayId");
        if (replayId?.length > 0) {
            this.wrapperController.startGameReplayFromId(replayId);
        }
    }
    addView(type) {
        let view = this.getView(type);
        if (view) {
            this.addChild(view);
        }
    }
    removeView(type, destroy = false) {
        let view = this.getView(type);
        if (view) {
            this.removeChild(view);
            if (destroy) {
                this.views.remove(type);
                view.destroy();
            }
        }
    }
    reparentView(type) {
        this.removeView(type);
        this.addView(type);
    }
    getView(type) {
        return this.views.getValue(type);
    }
    addChild(child) {
        this.renderer.addChild(child);
    }
    removeChild(child) {
        this.renderer.removeChild(child);
    }
    registerCore(event, handler, scope) {
        this.core.addEventListener(event.getType(), handler, scope);
    }
    dispatchCore(event) {
        this.core.dispatchEvent(event);
    }
    registerGsapPlugins() {
        PixiPlugin.registerPIXI(PIXI);
        gsap.registerPlugin(Flip, ScrollTrigger, ScrollToPlugin, Draggable, MotionPathPlugin, EaselPlugin, PixiPlugin, TextPlugin, ExpoScaleEase, CustomEase);
    }
}
//# sourceMappingURL=App.js.map